import { useState } from "react";

import { BarChartRounded, TableRowsRounded } from "@mui/icons-material";
import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, Typography } from "@mui/material";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import { formatCategory, formatValue, humanizeKey } from "./format";
import { ResultTable } from "./ResultTable";

// Categorical palette in fixed order (validated for colour-vision deficiency on
// a white surface). Slots 3-5 are below 3:1 contrast, so every chart also
// offers a table view.
const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];
const SURFACE = "#ffffff";
const GRID = "#e8e8e6";
const TEXT_SECONDARY = "#52514e";
const AXIS_TICK = { fill: TEXT_SECONDARY, fontSize: 12, fontWeight: 500 };
const BAR_SIZE = 24;

function ChartTooltip({ active, payload, label, spec, categories }) {
	if (!active || !payload?.length) {
		return null;
	}
	const heading = spec.type === "pie" ? payload[0].name : formatCategory(label, categories);
	return (
		<Paper elevation={3} sx={{ px: 1.5, py: 1, minWidth: 140 }}>
			<Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
				{heading}
			</Typography>
			{payload.map((entry) => (
				<Stack key={entry.dataKey ?? entry.name} direction="row" spacing={1} alignItems="center">
					<Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: entry.payload?.fill ?? entry.color, flexShrink: 0 }} />
					<Typography variant="body2" sx={{ color: TEXT_SECONDARY, flexGrow: 1 }}>
						{spec.type === "pie" ? labelFor(spec, spec.series[0].key) : entry.name}
					</Typography>
					<Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
						{formatValue(entry.value, spec.value_format)}
					</Typography>
				</Stack>
			))}
		</Paper>
	);
}

// Legend entries keep text in text ink; the dot carries the series colour.
const legendText = (value) => <span style={{ color: TEXT_SECONDARY }}>{value}</span>;

function labelFor(spec, key) {
	return spec.series.find((s) => s.key === key)?.label ?? humanizeKey(key);
}

function truncateLabel(value, max = 34) {
	const text = String(value ?? "");
	return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function renderPlot(spec) {
	const { type, data, x_key: xKey, series, value_format: valueFormat } = spec;
	const categories = data.map((row) => row[xKey]);
	const tooltip = <Tooltip content={<ChartTooltip spec={spec} categories={categories} />} cursor={{ fill: "rgba(0,0,0,0.04)", stroke: GRID }} />;
	const legend = series.length > 1 ? <Legend iconType="circle" iconSize={8} formatter={legendText} wrapperStyle={{ fontSize: 12 }} /> : null;
	const valueTick = (value) => formatValue(value, valueFormat, { compact: true });
	const grid = <CartesianGrid stroke={GRID} vertical={false} />;
	const xAxis = (
		<XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} tickFormatter={(v) => truncateLabel(formatCategory(v, categories), 14)} minTickGap={12} />
	);
	const yAxis = <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={valueTick} width={56} />;

	if (type === "horizontal_bar") {
		return (
			<BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }} barGap={2} barCategoryGap="20%">
				<CartesianGrid stroke={GRID} horizontal={false} />
				<XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={valueTick} />
				<YAxis type="category" dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} width={230} tickFormatter={(v) => truncateLabel(v)} interval={0} />
				{tooltip}
				{legend}
				{series.map((s, i) => (
					<Bar key={s.key} dataKey={s.key} name={labelFor(spec, s.key)} fill={SERIES_COLORS[i]} radius={[0, 4, 4, 0]} maxBarSize={BAR_SIZE} isAnimationActive={false} />
				))}
			</BarChart>
		);
	}
	if (type === "bar") {
		return (
			<BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }} barGap={2} barCategoryGap="20%">
				{grid}
				{xAxis}
				{yAxis}
				{tooltip}
				{legend}
				{series.map((s, i) => (
					<Bar key={s.key} dataKey={s.key} name={labelFor(spec, s.key)} fill={SERIES_COLORS[i]} radius={[4, 4, 0, 0]} maxBarSize={BAR_SIZE} isAnimationActive={false} />
				))}
			</BarChart>
		);
	}
	if (type === "line") {
		return (
			<LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
				{grid}
				{xAxis}
				{yAxis}
				{tooltip}
				{legend}
				{series.map((s, i) => (
					<Line
						key={s.key}
						dataKey={s.key}
						name={labelFor(spec, s.key)}
						stroke={SERIES_COLORS[i]}
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
						dot={data.length <= 12 ? { r: 4, fill: SERIES_COLORS[i], stroke: SURFACE, strokeWidth: 2 } : false}
						activeDot={{ r: 5, fill: SERIES_COLORS[i], stroke: SURFACE, strokeWidth: 2 }}
						isAnimationActive={false}
					/>
				))}
			</LineChart>
		);
	}
	if (type === "area") {
		return (
			<AreaChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
				{grid}
				{xAxis}
				{yAxis}
				{tooltip}
				{legend}
				{series.map((s, i) => (
					<Area
						key={s.key}
						dataKey={s.key}
						name={labelFor(spec, s.key)}
						stroke={SERIES_COLORS[i]}
						strokeWidth={2}
						fill={SERIES_COLORS[i]}
						fillOpacity={series.length > 1 ? 0.12 : 0.18}
						activeDot={{ r: 5, fill: SERIES_COLORS[i], stroke: SURFACE, strokeWidth: 2 }}
						isAnimationActive={false}
					/>
				))}
			</AreaChart>
		);
	}
	// pie
	const valueKey = series[0].key;
	return (
		<PieChart>
			{tooltip}
			<Legend iconType="circle" iconSize={8} layout="vertical" align="right" verticalAlign="middle" formatter={legendText} wrapperStyle={{ fontSize: 12 }} />
			<Pie data={data} dataKey={valueKey} nameKey={xKey} innerRadius="55%" outerRadius="85%" stroke={SURFACE} strokeWidth={2} isAnimationActive={false}>
				{data.map((row, i) => (
					<Cell key={String(row[xKey])} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
				))}
			</Pie>
		</PieChart>
	);
}

export function InsightChart({ spec }) {
	const [view, setView] = useState("chart");
	const columns = [spec.x_key, ...spec.series.map((s) => s.key)];
	const height = spec.type === "horizontal_bar" ? Math.min(Math.max(spec.data.length * 34 + 40, 180), 560) : 300;

	return (
		<Paper variant="outlined" sx={{ p: 2, my: 1.5 }}>
			<Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 1.5 }}>
				<Box sx={{ flexGrow: 1, minWidth: 0 }}>
					<Typography variant="h4" component="h3" sx={{ fontSize: 16, m: 0 }}>
						{spec.title}
					</Typography>
					{spec.subtitle && (
						<Typography variant="body2" sx={{ color: TEXT_SECONDARY, mt: 0.5 }}>
							{spec.subtitle}
						</Typography>
					)}
				</Box>
				<ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, next) => next && setView(next)} aria-label="Chart or table view">
					<ToggleButton value="chart" aria-label="Chart view">
						<MuiTooltip title="Chart">
							<BarChartRounded fontSize="small" />
						</MuiTooltip>
					</ToggleButton>
					<ToggleButton value="table" aria-label="Table view">
						<MuiTooltip title="Table">
							<TableRowsRounded fontSize="small" />
						</MuiTooltip>
					</ToggleButton>
				</ToggleButtonGroup>
			</Stack>
			{view === "chart" ? (
				<Box sx={{ width: "100%", height }} role="img" aria-label={spec.title}>
					<ResponsiveContainer>{renderPlot(spec)}</ResponsiveContainer>
				</Box>
			) : (
				<ResultTable columns={columns} rows={spec.data} maxHeight={height} />
			)}
		</Paper>
	);
}
